import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import 'moment-timezone';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useEventLogging } from '../../../models/Logging';
import { debounce, isValidEmail } from '../../../models/UiUtils';
import { Button as LegacyButton } from '../../../web/components/Button';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { BookingGraphic } from '../../../web/components/svgs/graphics/BookingGraphic';
import { ITimezoneData } from '../../../web/components/TimezonePicker';
import { Button } from '../../../web/components-v2/Button';
import '../../../web/styles/inputs.less';
import { CustomPageType } from '../../../extViewmodels/sdk/models/CustomPageStyle';
import { baseStyleSheet } from '../../../web/styles/styles';
import { useErrorMessage } from '../../contexts/errorMessage';
import { useMeeting } from '../../contexts/meeting';
import { useCustomPageStyle } from '../../hooks/useCustomPageStyle';
import { ScheduleMeetingAvailabilityPicker } from '../../ScheduleMeetingAvailabilityPicker';
import { ScheduleMeetingDayPicker } from '../../ScheduleMeetingDayPicker';
import { ScheduleMeetingHeader } from '../../ScheduleMeetingHeader';
import { ScheduleMeetingHeaderGroup } from '../../ScheduleMeetingHeaderGroup';
import { ScheduleMeetingLocation } from '../../ScheduleMeetingLocation/Index';
import { ScheduleMeetingNotFound } from '../../ScheduleMeetingNotFound';
import { ScheduleMeetingUserInfo } from '../../ScheduleMeetingUserInfo';
import { MobileBreakpoint, styleSheet as pageStyles } from '../../styles/styles';
import { IAvailabilitySlot, ITeamMemberAvailabilities } from '../../types';
import { Breakpoint, calculateTeamTimeSlots, getMonthFromOffset, getSelectedDateAsString } from '../../UiUtils';
import { MeetingContainer } from '../MeetingContainer';
import { styleSheet } from './styles';

const getBreakpoint = () => (window.innerWidth <= MobileBreakpoint ? 'mobile' : 'desktop');

const TRADESHOW_URL_KEY = 'tradeshowUrl';
const getRedirectUrl = () => {
	return sessionStorage.getItem(TRADESHOW_URL_KEY) ?? '';
};
const setRedirectUrl = () => {
	sessionStorage.setItem(TRADESHOW_URL_KEY, window.location.href);
};

export const ScheduleMeetingApp = () => {
	const location = useLocation();
	// Tradeshow mode allows the user to go back to the same calendar instead of closing the tab
	//   To enable simply put a query string param that includes 'tradeshow'
	//   ex #/123/30-minute-meeting?tradeshow
	const tradeshowMode = Boolean(location.search?.includes('tradeshow'));

	const meetingContext = useMeeting();

	const {
		isLoading,
		meeting,
		altRequestMeeting,
		setMainParticipant,
		setDateTime,
		getTeamAvailabilities,
		scheduleMeeting,
		isBusy: isUpdatingMeeting,
	} = meetingContext;

	const { hasCustomContent: hasCustomHeader } = useCustomPageStyle(meetingContext.shortCode, CustomPageType.Header);
	const { hasCustomContent: hasCustomFooter } = useCustomPageStyle(meetingContext.shortCode, CustomPageType.Footer);
	const meetingContextRef = useRef(meetingContext);
	meetingContextRef.current = meetingContext;

	const { setError } = useErrorMessage();
	const h = useHistory();
	const logger = useEventLogging();
	const [selectedTimezone, setSelectedTimezone] = useState<string>(moment.tz.guess());
	const [selectedDate, setSelectedDate] = useState<moment.Moment>(moment());
	const [selectedAvailability, setSelectedAvailability] = useState<IAvailabilitySlot>(undefined);
	const [userInfo, setUserInfo] = useState<Api.ISchedulerContact>(undefined);
	const [inviteeSelectedMeetingLocation, setInviteeSelectedMeetingLocation] = useState<Api.MeetingLocation>(null);
	const [breakpoint, setBreakpoint] = useState<Breakpoint>(getBreakpoint());
	const [currentStep, setCurrentStep] = useState(1);
	const [scheduling, setScheduling] = useState(false);
	const [fetchingMeetingInfo, setFetchingMeetingInfo] = useState(false);
	const [fetchingAvailabilities, setFetchingAvailabilities] = useState(false);
	const [mounted, setMounted] = useState(false);
	const [currentOffset, setOffset] = useState(0);
	const [teamAvailabilities, setTeamAvailabilities] = useState<ITeamMemberAvailabilities[]>([]);
	const [fatalError, setFatalError] = useState(false);

	const IS_FETCHING = fetchingMeetingInfo || fetchingAvailabilities;

	const updateBreakpoint = () => {
		debounce(() => {
			const updatedBreakpoint = getBreakpoint();
			if (breakpoint !== updatedBreakpoint) {
				setBreakpoint(updatedBreakpoint);
			}
		}, 50)();
	};

	useEffect(() => {
		setFetchingMeetingInfo(true);
		const { init } = meetingContextRef.current;

		init(selectedTimezone)
			.catch((err: Api.IOperationResultNoValue) => {
				logger.logApiError('SchedulerInit-Error', err);
				setFatalError(true);
			})
			.finally(() => {
				setFetchingMeetingInfo(false);
				setMounted(true);
			});

		return () => {
			const { reset } = meetingContextRef.current;
			reset();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);

	useEffect(() => {
		const { meeting: currentMeeting, shortCode } = meetingContextRef.current;
		if (!isLoading && currentMeeting) {
			if (shortCode?.includes('tm-') && currentMeeting.id) {
				if (tradeshowMode) {
					setRedirectUrl();
				}
				h.push({
					pathname: `/team/${currentMeeting.id}`,
					search: tradeshowMode ? '?tradeshow' : undefined,
				});
				return;
			}
			onGetAvailabilities(currentOffset, true);
			if (currentMeeting.contacts?.length && currentMeeting.contacts[0]?.timeZoneId) {
				setSelectedTimezone(currentMeeting?.contacts[0].timeZoneId);
			}

			const minStartMomemt = currentMeeting.minimumStartDate ? moment(currentMeeting.minimumStartDate) : null;
			if (minStartMomemt && minStartMomemt.isAfter(moment().endOf('day'))) {
				setSelectedDate(minStartMomemt);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading]);

	useEffect(() => {
		window.addEventListener('resize', updateBreakpoint);
		return () => window.removeEventListener('resize', updateBreakpoint);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [breakpoint]);

	const isInviteeLocationSelectionRequired = meeting?.location?._type === Api.MeetingType.AllowInvitee;
	const meetingLocationValid = isInviteeLocationSelectionRequired ? inviteeSelectedMeetingLocation : true;

	const allInfoEntered =
		userInfo &&
		userInfo?.firstName &&
		userInfo?.lastName &&
		userInfo?.phoneNumber &&
		userInfo?.emailAddress &&
		isValidEmail(userInfo?.emailAddress) &&
		meetingContext.mainParticipant?.firstName &&
		meetingContext.mainParticipant?.lastName &&
		meetingContext.mainParticipant?.phoneNumber &&
		meetingContext.mainParticipant?.emailAddress &&
		meetingLocationValid &&
		(!meeting?.commentsRequired || meeting?.comments);

	const handleClose = () => {
		if (window.opener) {
			try {
				opener.location.reload();
			} catch {
				// Opener is cross-origin; browsers block reading Location properties.
				// Nothing we can do to refresh it from here, so just close.
			}
		}
		window.close();
	};

	// Currently, only show the close button if the window was opened by another window.
	const showCloseButton = window.opener;

	const getEnabledDays = useCallback(() => {
		if (meetingContext.teamMeetingId) {
			if (teamAvailabilities?.length) {
				const days = teamAvailabilities[0]?.availabilities?.length;
				const enabledDays = new Array(days).fill(false);
				for (const teamMemberAvailability of teamAvailabilities) {
					if (teamMemberAvailability?.availabilities?.length === days) {
						teamMemberAvailability?.availabilities?.forEach((availability, i) => {
							if (availability?.length) {
								enabledDays[i] = true;
							}
						});
					}
				}
				return enabledDays;
			}
			return [];
		}
		return meeting?.availabilities?.map(x => !!x?.length);
	}, [meeting?.availabilities, meetingContext.teamMeetingId, teamAvailabilities]);

	const getAvailabilities = useCallback(() => {
		if (!meeting?.availabilities?.length || !selectedDate) {
			return [];
		}

		return (meeting?.availabilities[selectedDate.date() - 1] || [])?.map(a => {
			return { ids: [] as string[], priority: 0, time: moment(a.start) };
		});
	}, [meeting?.availabilities, selectedDate]);

	const getTeamTimeSlots = useCallback(() => {
		return calculateTeamTimeSlots(teamAvailabilities, selectedDate);
	}, [teamAvailabilities, selectedDate]);

	const goToStep = (step: number) => () => {
		if (step === 1) {
			onGetAvailabilities(currentOffset);
		}

		setCurrentStep(step);
	};

	const onAvailabilityChange = (availability: IAvailabilitySlot) => {
		setSelectedAvailability(availability);
		if (selectedDate && breakpoint === 'mobile') {
			setDateTime(availability.time.toDate(), availability.ids).catch((err: Api.IOperationResultNoValue) => {
				logger.logApiError('SchedulerAvailabilityChange-Error', err);
				setError(err);
			});
			setCurrentStep(3);
		}
	};

	const onAltSubmit = useCallback(() => {
		if (allInfoEntered) {
			setScheduling(true);
			altRequestMeeting(userInfo)
				.then(() => {
					setCurrentStep(5);
					setScheduling(false);
				})
				.catch((err: Api.IOperationResultNoValue) => {
					logger.logApiError('SchedulerScheduleMeeting-Error', err);
					setError(err);
				});
		}
	}, [allInfoEntered, altRequestMeeting, logger, setError, userInfo]);

	const onDayChange = (date: moment.Moment) => {
		if (date) {
			setSelectedAvailability(null);
			setSelectedDate(date);
			setCurrentStep(2);
		}
	};

	const onMonthChange = (offset: number) => {
		setSelectedAvailability(null);
		setSelectedDate(null);
		onGetAvailabilities(currentOffset + offset);
	};

	const onWillChangeToNextMonth = () => onMonthChange(1);

	const onWillChangeToPreviousMonth = () => onMonthChange(-1);

	const onError = () => {
		setCurrentStep(99);
	};

	const teamAvailabilitiesRef = useRef(teamAvailabilities);
	teamAvailabilitiesRef.current = teamAvailabilities;

	const onGetAvailabilities = useCallback(
		async (offset: number, initialLoad?: boolean) => {
			const {
				meeting: currentMeeting,
				teamMeetingId,
				getAvailabilities: getCurrentAvailabilities,
			} = meetingContextRef.current;
			setFetchingAvailabilities(true);
			if (offset !== currentOffset) {
				setOffset(offset);
			}
			try {
				if (teamMeetingId) {
					setTeamAvailabilities([]);
					let newTeamAvailabilities: ITeamMemberAvailabilities[] = [];
					if (currentMeeting?.meetingConfigs?.length) {
						newTeamAvailabilities = (
							await Promise.allSettled(
								currentMeeting?.meetingConfigs.map(config => getTeamAvailabilities(offset, config))
							)
						)
							.map(x => (x.status === 'fulfilled' ? x.value : null))
							.filter(Boolean);

						if (newTeamAvailabilities.length === 0) {
							throw new Error('No valid meeting configs to load');
						}

						setTeamAvailabilities(newTeamAvailabilities);
					} else {
						throw new Error('No meeting configs to load from');
					}
				} else {
					const updatedMeetings = await getCurrentAvailabilities(offset);
					if (initialLoad) {
						// no availabilities were found for the currently selected month. Load next month
						if (updatedMeetings.availabilities?.filter(x => !!x).length > 0) {
							let month: moment.Moment;
							for (const a of updatedMeetings.availabilities) {
								if (a) {
									month = moment(a[0].start);
									break;
								}
							}
							setSelectedDate(month);
						} else {
							if (offset <= 2) {
								onGetAvailabilities(offset + 1, true);
							}
						}
					}
				}
				setFetchingAvailabilities(false);
			} catch (err) {
				logger.logApiError('SchedulerGetAvailabilities-Error', err);
				onError();
				setFetchingAvailabilities(false);
			}
		},
		[currentOffset, getTeamAvailabilities, logger]
	);

	const onNextClick = useCallback(() => {
		if (selectedDate && selectedAvailability) {
			setDateTime(selectedAvailability.time.toDate(), selectedAvailability.ids).catch(
				(err: Api.IOperationResultNoValue) => {
					logger.logApiError('SchedulerAvailabilityChange-Error', err);
					setError(err);
				}
			);
			setCurrentStep(3);
		}
	}, [selectedDate, selectedAvailability, setDateTime, logger, setError]);

	const onScheduleMeetingClick = useCallback(() => {
		if (allInfoEntered && !isUpdatingMeeting) {
			setScheduling(true);
			scheduleMeeting()
				.then(() => {
					setCurrentStep(4);
					setScheduling(false);
				})
				.catch((err: Api.IOperationResultNoValue) => {
					logger.logApiError('SchedulerScheduleMeeting-Error', err);
					setError(err);
					setScheduling(false);
				});
		}
	}, [allInfoEntered, scheduleMeeting, logger, setError, isUpdatingMeeting]);

	const onTimezoneChange = useCallback(
		(timezone: ITimezoneData) => {
			setSelectedTimezone(timezone.name);
			setMainParticipant({
				...meetingContext.mainParticipant,
				timeZoneId: timezone.name,
			}).catch(err => {
				logger.logApiError('SchedulerScheduleMeeting-Error', err);
				setError(err);
			});
		},
		[logger, meetingContext.mainParticipant, setError, setMainParticipant]
	);

	const getSelectedMonth = useCallback(() => {
		return getMonthFromOffset(currentOffset);
	}, [currentOffset]);

	const reloadFromRedirect = () => {
		const redirectUrl = getRedirectUrl();

		if (tradeshowMode && redirectUrl) {
			setSelectedDate(moment());
			setSelectedAvailability(null);
			setCurrentStep(1);
			window.location.replace(redirectUrl);
		}
	};

	const renderContent = () => {
		if (!mounted) {
			return null;
		}

		const availableAvailabilities = meetingContext.teamMeetingId ? getTeamTimeSlots() : getAvailabilities();

		const showPriority =
			!!meetingContext.teamMeetingId &&
			!meeting?.hidePriorities &&
			!!selectedDate &&
			availableAvailabilities.length > 0;

		const scheduledOutcome = meetingContext.isReschedule ? 'rescheduled' : 'scheduled';

		return (
			<>
				<div
					className={css(
						styleSheet.main,
						(currentStep === 3 || currentStep === 4 || currentStep === 5 || currentStep === 99) && styleSheet.flexCenter
					)}
				>
					{(currentStep === 1 || currentStep === 2) && (
						<>
							<ScheduleMeetingDayPicker
								allowSameDay={meetingContext.allowSameDay}
								breakpoint={breakpoint}
								enabledDays={getEnabledDays()}
								hide={!showCalendar()}
								onDayChange={onDayChange}
								onWillChangeToNextMonth={onWillChangeToNextMonth}
								onWillChangeToPreviousMonth={onWillChangeToPreviousMonth}
								processing={fetchingMeetingInfo}
								selectedDate={selectedDate}
								selectedMonth={getSelectedMonth()}
							/>
							<ScheduleMeetingAvailabilityPicker
								availabilities={availableAvailabilities}
								breakpoint={breakpoint}
								goToStep={goToStep}
								hide={!showAvailabilities()}
								onAvailabilityChange={onAvailabilityChange}
								onTimezoneChange={onTimezoneChange}
								processing={fetchingMeetingInfo}
								selectedAvailability={selectedAvailability}
								selectedDate={selectedDate}
								selectedTimezone={selectedTimezone}
								showPriority={showPriority}
							/>
						</>
					)}
					{(currentStep === 3 || currentStep === 99) && (
						<div className={css(styleSheet.userInfoContainer)}>
							<ScheduleMeetingUserInfo includeAltFields={currentStep === 99} onInfoChange={onUserInfoChange} />
							{meeting?.location?._type === Api.MeetingType.AllowInvitee && (
								<ScheduleMeetingLocation
									inviteeSelectedMeetingLocation={inviteeSelectedMeetingLocation}
									setInviteeSelectedMeetingLocation={setInviteeSelectedMeetingLocation}
								/>
							)}
							{scheduling && <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} />}
						</div>
					)}
					{(currentStep === 4 || currentStep === 5) && (
						<div className={css(styleSheet.confirmationContainer)}>
							<BookingGraphic />
							{currentStep === 4 ? (
								<>
									<h2>Thanks! The meeting has been {scheduledOutcome}.</h2>
									<div className={css(styleSheet.text)}>{meeting?.host}</div>
									<div className={css(pageStyles.pill, styleSheet.marginBottom20)}>
										<time dateTime={`${selectedAvailability?.time}`}>
											{selectedDate && selectedAvailability?.time
												? `${getSelectedDateAsString(
														selectedAvailability.time,
														breakpoint
													)} at ${selectedAvailability.time.format('h:mm A')}`
												: ''}
										</time>
									</div>

									{meetingContext.teamMeetingId ? (
										<p className={css(styleSheet.text)}>A calendar invite has been sent.</p>
									) : meeting?.calendarInviteSentExternally ? null : (
										<>
											<p className={css(styleSheet.text)}>{`We will send you ${
												(meetingContext.guestParticipants || []).length > 0 ? 'and your guests' : ''
											} a calendar invite shortly.`}</p>
											<a
												className={css(baseStyleSheet.brandLink, styleSheet.downloadLink)}
												href={`${meetingContext.baseUrl}/scheduler/${meeting?.id}/ical`}
												target='_blank'
												rel='noreferrer'
											>
												Download iCal
											</a>
										</>
									)}
								</>
							) : (
								<div>{`Thanks! We've notified ${
									meeting?.host ? meeting?.host.split(' ')[0] : 'the host of this meeting.'
								}.`}</div>
							)}
						</div>
					)}
				</div>
				<footer className={css(styleSheet.footer, currentStep === 3 && styleSheet.step3Footer)}>
					{(currentStep === 1 || currentStep === 2) && (
						<Button
							variant='primary'
							selectorClassName='nextCta'
							disabled={!selectedDate || !selectedAvailability}
							onClick={onNextClick}
						>
							Next
						</Button>
					)}
					{currentStep === 3 && (
						<>
							<div className={css(styleSheet.footerSide)}>
								<div className={css(styleSheet.dateAndTime)}>
									{selectedAvailability?.time &&
										`${getSelectedDateAsString(selectedDate, breakpoint)} at ${selectedAvailability.time.format('h:mm A')}`}
								</div>
								<LegacyButton
									className={css(baseStyleSheet.brandLink)}
									kind='custom'
									label='Change'
									onClick={goToStep(1)}
								/>
							</div>
							<Button
								variant='primary'
								disabled={!allInfoEntered || isUpdatingMeeting}
								onClick={onScheduleMeetingClick}
							>
								{meetingContext.isReschedule ? 'Reschedule Meeting' : 'Schedule Meeting'}
							</Button>
							<div className={css(styleSheet.footerSide)} />
						</>
					)}

					{currentStep === 4 ? (
						<div>
							{tradeshowMode ? (
								<Button variant='primary' onClick={reloadFromRedirect}>
									Return to Booking Page
								</Button>
							) : showCloseButton ? (
								<Button variant='primary' onClick={handleClose}>
									Close
								</Button>
							) : null}
						</div>
					) : null}

					{currentStep === 99 && (
						<Button variant='primary' onClick={onAltSubmit}>
							Submit
						</Button>
					)}
				</footer>
				{/* Disclosure text displayed below buttons */}
				{meeting?.meetingDisclosure &&
					(currentStep === 1 || currentStep === 2 || currentStep === 3 || currentStep === 99) && (
						<div className={css(styleSheet.disclosureText)}>{meeting.meetingDisclosure}</div>
					)}
			</>
		);
	};

	const onUserInfoChange = useCallback(
		(info: Api.ISchedulerContact) => {
			setUserInfo(info);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[userInfo]
	);

	const showAvailabilities = useCallback(() => {
		return currentStep === 2 || (breakpoint === 'desktop' && currentStep === 1);
	}, [currentStep, breakpoint]);

	const showCalendar = useCallback(() => {
		return currentStep === 1 || (breakpoint === 'desktop' && currentStep === 2);
	}, [currentStep, breakpoint]);

	if (fatalError || (!meetingContext.shortCode && !meetingContext.teamMeetingId)) {
		return <ScheduleMeetingNotFound />;
	}

	return (
		<MeetingContainer shortCode={meetingContext.shortCode}>
			<div
				className={css(
					styleSheet.mainContainer,
					pageStyles.sharedBackgroundWrapper,
					(hasCustomHeader || hasCustomFooter) && pageStyles.sharedBackgroundWrapperMobileNoCustom
				)}
			>
				<div className={css(pageStyles.container)}>
					{meetingContext.teamMeetingId ? (
						<ScheduleMeetingHeaderGroup />
					) : (
						<ScheduleMeetingHeader fetching={fetchingMeetingInfo} />
					)}
					<div className={css(styleSheet.body)}>{IS_FETCHING ? <LoadingSpinner /> : renderContent()}</div>
				</div>
			</div>
		</MeetingContainer>
	);
};
